import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  ClockIcon,
  AcademicCapIcon,
  PencilIcon,
  PlayIcon,
  CalendarIcon,
  UserGroupIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const QuizDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/quizzes/${id}`)
      .then(res => {
        const quizData = res.data;
        // Parse options if they're stored as strings
        if (quizData.questions) {
          quizData.questions = quizData.questions.map(q => {
            if (q.options && typeof q.options === 'string') {
              try {
                q.options = JSON.parse(q.options);
              } catch (e) {
                console.error('Failed to parse options for question:', q.id, e);
                q.options = [];
              }
            }
            // Ensure options is always an array
            if (!Array.isArray(q.options)) {
              q.options = [];
            }
            return q;
          });
        }
        setQuiz(quizData);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.error || err.message);
        setLoading(false);
      });
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getQuizStatus = () => {
    if (!quiz) return { status: 'unknown', color: 'gray' };
    const now = new Date();
    const openTime = quiz.openTime ? new Date(quiz.openTime) : null;
    const closeTime = quiz.closeTime ? new Date(quiz.closeTime) : null;
    
    if (openTime && now < openTime) return { status: 'upcoming', color: 'blue' };
    if (closeTime && now > closeTime) return { status: 'closed', color: 'gray' };
    return { status: 'active', color: 'green' };
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading quiz details...</p>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md">
        <div className="flex items-center mb-2">
          <div className="bg-red-100 rounded-full p-2 mr-3">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-red-800 font-medium">Error Loading Quiz</h3>
        </div>
        <p className="text-red-600">{error}</p>
      </div>
    </div>
  );

  const status = getQuizStatus();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <DocumentTextIcon className="w-8 h-8 text-blue-600 mr-3" />
                  {quiz.title}
                </h1>
                <div className="flex items-center mt-2 space-x-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize
                    ${status.color === 'green' ? 'bg-green-100 text-green-800' : 
                      status.color === 'blue' ? 'bg-blue-100 text-blue-800' : 
                      'bg-gray-100 text-gray-800'}`}>
                    {status.status}
                  </span>
                  {quiz.course && (
                    <span className="text-sm text-gray-600 flex items-center">
                      <AcademicCapIcon className="w-4 h-4 mr-1" />
                      {quiz.course.title}
                    </span>
                  )}
                  {quiz.studentType && (
                    <span className="text-sm text-gray-600 flex items-center">
                      <UserGroupIcon className="w-4 h-4 mr-1" />
                      {quiz.studentType.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to={`/quiz/${quiz.id}/attempt`}
                className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors duration-200"
              >
                <PlayIcon className="w-5 h-5 mr-2" />
                Take Quiz
              </Link>
              <Link
                to={`/quiz/${quiz.id}/edit`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <PencilIcon className="w-5 h-5 mr-2" />
                Edit
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quiz Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
                Quiz Information
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Quiz ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">#{quiz.id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Student Level</dt>
                  <dd className="mt-1 text-sm text-gray-900">{quiz.studentType?.toUpperCase() || 'Not specified'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Time Limit</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {quiz.timeLimit ? `${quiz.timeLimit} minutes` : 'No time limit'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Max Attempts</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {quiz.maxAttempts ? `${quiz.maxAttempts} attempt${quiz.maxAttempts > 1 ? 's' : ''}` : 'Unlimited'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Questions Per Page</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {quiz.questionsPerPage || 'All questions on one page'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total Questions</dt>
                  <dd className="mt-1 text-sm text-gray-900">{quiz.questions?.length || 0}</dd>
                </div>
              </div>
            </div>

            {/* Timed Sections */}
            {quiz.timedSections && quiz.timedSections.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <ClockIcon className="w-5 h-5 text-blue-600 mr-2" />
                  Timed Sections
                </h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                    {JSON.stringify(quiz.timedSections, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Questions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <QuestionMarkCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
                Questions ({quiz.questions?.length || 0})
              </h2>
              {quiz.questions && quiz.questions.length > 0 ? (
                <div className="space-y-6">
                  {quiz.questions.map((q, index) => (
                    <div key={q.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 mt-1">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-gray-900 font-medium mb-3">{q.text}</p>
                          {q.options && q.options.length > 0 ? (
                            <div className="space-y-2">
                              {q.options.map((opt, idx) => (
                                <div key={idx} className={`flex items-center p-2 rounded ${
                                  opt === q.answer ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                                }`}>
                                  <span className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs font-medium mr-3">
                                    {String.fromCharCode(65 + idx)}
                                  </span>
                                  <span className={`flex-1 ${opt === q.answer ? 'text-green-800 font-medium' : 'text-gray-700'}`}>
                                    {opt}
                                  </span>
                                  {opt === q.answer && (
                                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <p className="text-sm text-gray-600 mb-1">Expected Answer:</p>
                              <p className="text-gray-900">{q.answer || 'No answer provided'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <QuestionMarkCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No questions available for this quiz</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Schedule */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CalendarIcon className="w-5 h-5 text-blue-600 mr-2" />
                Schedule
              </h3>
              <div className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Opens</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(quiz.openTime)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Closes</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(quiz.closeTime)}
                  </dd>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Questions</span>
                  <span className="text-sm font-medium text-gray-900">{quiz.questions?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Multiple Choice</span>
                  <span className="text-sm font-medium text-gray-900">
                    {quiz.questions?.filter(q => q.options && q.options.length > 0).length || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Free Response</span>
                  <span className="text-sm font-medium text-gray-900">
                    {quiz.questions?.filter(q => !(q.options && q.options.length > 0)).length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizDetailPage;
